import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import { ObjectGraphType } from '@jet-app/music-classical/dependencies';
import type { Opt } from '@jet/environment';
import { getBaseSeoData } from './BaseSeoData';
import type { CropCode, SeoData, TrackRecordSEO } from './types';
import type { PlaylistPage } from '@jet-app/music-classical/models/pages/playlist';
import {
    createActionSchema,
    ACTION_SCHEMA_TYPE,
    getTracksForSEO,
    schemaDataForTrack,
    getTrackOpenGraphData,
} from '@jet-app/music-classical/utilities/seo/utils';
import { formatDuration, getDurationFromTracks } from './duration';
import type { TrackLockupCollection } from '@jet-app/music-classical/models/section/track-lockup/TrackLockupCollection';

/**
 * @method getPlaylistSeoData
 * @param {string} url Playlist canonical URL
 * @param {PlaylistPage} page playlist page object
 * @param {ObjectGraph} objectGraph
 * @returns {SeoData}
 */
export function getPlaylistSeoData(
    url: string,
    page: PlaylistPage,
    objectGraph: ObjectGraph,
): SeoData {
    const localization = inject(ObjectGraphType.localization, objectGraph);
    const baseSeoData: SeoData = getBaseSeoData(url, objectGraph, {
        ogType: 'music.playlist',
    });
    const playlistId: string = page.header.id;
    const playlistName: string = page.header.title;
    const curatorName: string =
        page.header.subtitle || page.header.subtitleLinks?.[0]?.title || '';

    // Ensure we are only working with track lockups.  There may be others,
    // such as footers, etc. that we won't need to generate SEO from.
    const trackLockups: TrackLockupCollection[] = page.sections?.filter(
        (section) => section.itemKind === 'trackLockup',
    );

    const tracks: TrackRecordSEO[] = getTracksForSEO(trackLockups);

    // Because works may contain multiple tracks, and each track lockup
    // is part of a work, use the flattened tracks collection to
    // determine proper length of tracks in a playlist.
    const songsCount = tracks.length;
    const tracksDuration = getDurationFromTracks(tracks);

    // Human-readable duration of the entire playlist.  Ex: 1 hour, 24 minutes
    const formattedDuration = formatDuration(
        tracksDuration / 1000,
        objectGraph,
    );

    function getPageTitle() {
        if (playlistName) {
            return localization.string(
                'ASE.Web.MusicClassicalClientJS.Meta.Playlist.Title',
                { playlistName },
            );
        }

        return baseSeoData.pageTitle;
    }

    function getPageDescription() {
        if (playlistName && songsCount && formattedDuration) {
            return localization.string(
                'ASE.Web.MusicClassicalClientJS.Meta.Playlist.Description',
                {
                    playlistName,
                    songsCount,
                    duration: formattedDuration,
                },
            );
        }

        return baseSeoData.description;
    }

    const pageTitle: string = getPageTitle();
    const description: Opt<string> = getPageDescription();
    const artworkUrl: Opt<string> = page.header.artwork?.template;
    const crop: CropCode = 'bb';

    /**
     *
     * Generate SEO data object
     *
     */
    return {
        ...baseSeoData,
        pageTitle,
        description,

        /**
         * Social OG Tags
         */
        socialTitle: pageTitle,
        socialDescription: description,
        ogSongs: tracks?.map((track) => getTrackOpenGraphData(track)),
        ogType: 'music.playlist',
        songsCount,

        /**
         *
         * Apple-specific Values
         *
         */
        appleContentId: playlistId,
        appleTitle: pageTitle,
        appleDescription: description,

        /**
         *
         * Artwork Values
         *
         */
        crop,
        twitterCropCode: crop,
        artworkUrl: artworkUrl ?? baseSeoData.artworkUrl,

        /**
         *
         * Structured Data/schema.org Values
         *
         */
        schemaName: 'schema:music-playlist',
        schemaContent: {
            '@context': 'http://schema.org',
            '@type': 'MusicPlaylist',
            name: playlistName,
            description,
            image: artworkUrl,
            numTracks: songsCount,
            url,
            author: !!curatorName
                ? {
                      '@type': 'Person',
                      name: curatorName,
                  }
                : undefined,
            offers: {
                '@type': 'Offer',
                category: 'free',
                price: 0,
            },
            potentialAction: createActionSchema(url, ACTION_SCHEMA_TYPE.Listen),
            track: tracks?.map((track) => schemaDataForTrack(track)),
        },
    };
}

export function addingPlaylistSeoData(
    url: string,
    page: PlaylistPage,
    objectGraph: ObjectGraph,
): PlaylistPage {
    return {
        ...page,
        seoData: getPlaylistSeoData(url, page, objectGraph),
    };
}
